var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DataFrame } from '../../model/DataFrame.js';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart, ScatterChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent, DatasetComponent, } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { Chart, BarController, BarElement, LineController, LineElement, PointElement, PieController, DoughnutController, ArcElement, Filler, CategoryScale, LinearScale, Tooltip, Legend, Title, } from 'chart.js';
echarts.use([
    BarChart,
    LineChart,
    PieChart,
    ScatterChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    DatasetComponent,
    CanvasRenderer,
]);
Chart.register(BarController, BarElement, LineController, LineElement, PointElement, PieController, DoughnutController, ArcElement, Filler, CategoryScale, LinearScale, Tooltip, Legend, Title);
const NON_CARTESIAN = new Set(['pie', 'doughnut', 'radar', 'funnel', 'gauge']);
function hexToRgb(hex) {
    const h = (hex || '').replace('#', '');
    if (h.length !== 6)
        return { r: 31, g: 119, b: 180 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}
function rgbToHex(r, g, b) {
    const c = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return '#' + c(r) + c(g) + c(b);
}
function rgbToHsl(rgb) {
    let r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (mx + mn) / 2;
    if (mx !== mn) {
        const d = mx - mn;
        s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
        switch (mx) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    return { h, s, l };
}
function hslToHex(h, s, l) {
    let r, g, b;
    if (s === 0) {
        r = g = b = l;
    }
    else {
        const hue2rgb = (p, q, t) => {
            if (t < 0)
                t += 1;
            if (t > 1)
                t -= 1;
            if (t < 1 / 6)
                return p + (q - p) * 6 * t;
            if (t < 1 / 2)
                return q;
            if (t < 2 / 3)
                return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    return rgbToHex(r * 255, g * 255, b * 255);
}
function tintPalette(baseHex, n) {
    if (n <= 1)
        return [baseHex];
    const { h, s, l } = rgbToHsl(hexToRgb(baseHex));
    const lMin = Math.max(0.20, l - 0.30);
    const lMax = Math.min(0.85, l + 0.20);
    return Array.from({ length: n }, (_, i) => {
        const t = i / (n - 1);
        return hslToHex(h, s, lMin + (lMax - lMin) * t);
    });
}
let BasicGraph = class BasicGraph extends LitElement {
    constructor() {
        super(...arguments);
        this.showTitle = true;
        this.print = false;
        this._error = null;
        this._echartsInstance = null;
        this._chartjsInstance = null;
        this._resizeObserver = null;
    }
    render() {
        if (this._error) {
            return html `<div class="error">${this._error}</div>`;
        }
        if (this.graph?.type === 'CHARTJS') {
            return html `<div class="container"><canvas></canvas></div>`;
        }
        return html `<div class="container"></div>`;
    }
    firstUpdated() {
        this._renderChart();
        // Both ECharts and Chart.js need an explicit `resize()` call when
        // the host shrinks/grows — they sample the container size only at
        // init unless we re-poke them. Critical when the chart lives in a
        // collapsible panel (e.g. <kpi-dimension-card>'s chart slot) and
        // is initialised while the wrapper still animates open from 0.
        const container = this.shadowRoot?.querySelector('.container');
        if (container && typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => {
                this._echartsInstance?.resize();
                this._chartjsInstance?.resize();
            });
            this._resizeObserver.observe(container);
        }
    }
    updated(changed) {
        if (changed.has('graph') ||
            changed.has('data') ||
            changed.has('showTitle') ||
            changed.has('themeColor') ||
            changed.has('print')) {
            this._renderChart();
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
        this._disposeInstances();
    }
    _disposeInstances() {
        if (this._echartsInstance) {
            this._echartsInstance.dispose();
            this._echartsInstance = null;
        }
        if (this._chartjsInstance) {
            this._chartjsInstance.destroy();
            this._chartjsInstance = null;
        }
    }
    _renderChart() {
        if (!this.graph || !this.data)
            return;
        this._error = null;
        try {
            if (this.graph.type === 'ECHARTS') {
                this._renderECharts();
            }
            else if (this.graph.type === 'CHARTJS') {
                this._renderChartJs();
            }
            else {
                this._error = `Unsupported graph type: ${String(this.graph.type)}`;
            }
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            this._error = `Render error: ${msg}`;
        }
    }
    _config() {
        return (this.graph.config ?? {});
    }
    _extractColumn(name) {
        return this.data.rows.map((row) => row[name]);
    }
    _renderECharts() {
        if (this._chartjsInstance) {
            this._chartjsInstance.destroy();
            this._chartjsInstance = null;
        }
        const container = this.shadowRoot?.querySelector('.container');
        if (!container)
            return;
        if (!this._echartsInstance) {
            this._echartsInstance = echarts.init(container);
        }
        const { style = {}, mapping = {} } = this._config();
        const xCol = mapping.x;
        const yCols = mapping.y ?? [];
        const categories = xCol ? this._extractColumn(xCol) : [];
        const baseSeries = Array.isArray(style.series) ? style.series : [];
        const firstType = baseSeries[0]?.type;
        const isNonCartesian = firstType ? NON_CARTESIAN.has(firstType) : false;
        const series = yCols.map((col, i) => {
            const base = baseSeries[i] ?? { type: 'bar' };
            const yData = this._extractColumn(col);
            if (isNonCartesian) {
                const pairs = categories.map((name, j) => ({ name: String(name), value: yData[j] }));
                return { ...base, name: base.name ?? col, data: pairs };
            }
            // Inject theme color when no explicit color is set on the series.
            const themed = !base.itemStyle && this.themeColor
                ? { ...base, itemStyle: { color: this.themeColor } }
                : base;
            return { ...themed, name: themed.name ?? col, data: yData };
        });
        const option = { ...style, series };
        if (!this.showTitle) {
            delete option.title;
        }
        if (isNonCartesian) {
            delete option.xAxis;
            delete option.yAxis;
            // Derive a monochromatic palette from the theme color when the
            // style doesn't already define one.
            if (!option.color && this.themeColor) {
                option.color = tintPalette(this.themeColor, categories.length || 1);
            }
            if (this.print) {
                // Static render: drop the on-slice labels (they collide at report
                // sizes) — the host surface supplies its own legend — and let the plot
                // fill the box. The doughnut hole (radius array) is preserved.
                option.series = option.series.map((s) => ({
                    ...s,
                    center: ['50%', '50%'],
                    radius: Array.isArray(s.radius) ? ['40%', '74%'] : '74%',
                    label: { ...(s.label ?? {}), show: false },
                    labelLine: { ...(s.labelLine ?? {}), show: false },
                }));
            }
        }
        else {
            // Detect horizontal layout: xAxis.type='value' + yAxis.type='category'.
            const xDef = (style.xAxis ?? {});
            const yDef = (style.yAxis ?? {});
            const isHorizontal = xDef.type === 'value' && yDef.type === 'category';
            if (isHorizontal) {
                option.xAxis = this._withAxisDefaults({ ...xDef }, 'x');
                option.yAxis = this._withAxisDefaults({ ...yDef, data: categories }, 'y');
            }
            else {
                option.xAxis = this._withAxisDefaults({ type: 'category', ...xDef, data: categories }, 'x');
                option.yAxis = this._withAxisDefaults(yDef.type ? yDef : { type: 'value' }, 'y');
            }
        }
        this._applyLayoutDefaults(option, isNonCartesian);
        this._echartsInstance.setOption(option, true);
    }
    /**
     * Compact, non-overlapping axis defaults. These charts live in small
     * surfaces (KPI panel cards, map popups), so labels need a small font
     * and ECharts' overlap-hiding turned on — otherwise category ticks
     * collide. The saved style always wins where it sets a value.
     */
    _withAxisDefaults(axis, role) {
        const out = {
            ...axis,
            axisLabel: {
                fontSize: 10,
                hideOverlap: true,
                ...(axis.axisLabel ?? {}),
            },
        };
        // An axis name defaults to `nameLocation: 'end'`, which parks it at the far
        // tip of the axis. For the horizontal (x) axis that tip is the right edge,
        // where the tight KPI grid clips it (recette 81db839a: the value-axis name
        // "Population" rendered as "Pop"). Center the x-axis name under its axis so
        // it can never overflow the plot box — `_applyLayoutDefaults` reserves the
        // bottom room. The saved style wins if it pinned a location. (y-axis names
        // sit top-left at 'end' and don't clip, so we leave them untouched.)
        if (role === 'x' && out.name && out.nameLocation == null) {
            out.nameLocation = 'middle';
            out.nameGap = 24;
            out.nameTextStyle = { fontSize: 10, ...(out.nameTextStyle ?? {}) };
        }
        return out;
    }
    /**
     * Responsive defaults applied on top of the saved graph style so
     * charts read well when cramped: a scrolling legend instead of a
     * truncated one, a grid that reserves room for axis labels
     * (`containLabel`) and the legend, and tooltips confined to the
     * container. Saved-style values take precedence — these only fill
     * the gaps the style leaves.
     */
    _applyLayoutDefaults(option, isNonCartesian) {
        const lg = option.legend;
        if (lg && typeof lg === 'object' && !Array.isArray(lg)) {
            option.legend = {
                type: 'scroll',
                itemWidth: 12,
                itemHeight: 8,
                itemGap: 8,
                ...lg,
                textStyle: { fontSize: 10, ...(lg.textStyle ?? {}) },
                // Print is a static surface: a scrolling legend would hide overflow
                // entries behind a pager. Force a plain legend so it wraps and every
                // entry stays visible. Wins over any saved `type`.
                ...(this.print ? { type: 'plain' } : {}),
            };
        }
        const hasLegend = !!option.legend && option.legend.show !== false;
        if (!isNonCartesian) {
            // Our positional margins must win over the saved grid. Legacy
            // styles bake in offsets like `left: 100` to hand-reserve room
            // for axis labels — but we now enable `containLabel`, which
            // reserves that room automatically. Keeping both stacks the two
            // and shoves the plot off the left edge (bars stop aligning left
            // while pies, which use no grid, stay centred). The saved grid is
            // still spread first so any unrelated keys survive.
            // A centered x-axis name (see `_withAxisDefaults`) is drawn below the tick
            // labels, outside the `containLabel` box — reserve bottom room so it isn't
            // clipped. No name → keep the plot tight.
            const xHasName = !!(option.xAxis && option.xAxis.name);
            option.grid = {
                ...(option.grid ?? {}),
                left: 8,
                right: 14,
                top: hasLegend ? 46 : 12,
                bottom: xHasName ? 30 : 8,
                containLabel: true,
            };
        }
        if (option.tooltip == null) {
            option.tooltip = { confine: true };
        }
        else if (typeof option.tooltip === 'object' && option.tooltip.confine == null) {
            option.tooltip = { ...option.tooltip, confine: true };
        }
    }
    _renderChartJs() {
        if (this._echartsInstance) {
            this._echartsInstance.dispose();
            this._echartsInstance = null;
        }
        const canvas = this.shadowRoot?.querySelector('canvas');
        if (!canvas)
            return;
        const { style = {}, mapping = {} } = this._config();
        const xCol = mapping.x;
        const yCols = mapping.y ?? [];
        const chartType = style.type ?? 'bar';
        const isPolar = chartType === 'pie' || chartType === 'doughnut';
        const labels = xCol ? this._extractColumn(xCol) : [];
        const baseDatasets = Array.isArray(style?.data?.datasets) ? style.data.datasets : [];
        const datasets = yCols.map((col, i) => {
            const base = baseDatasets[i] ?? { label: col };
            const themed = { ...base };
            if (!base.backgroundColor && this.themeColor) {
                themed.backgroundColor = isPolar
                    ? tintPalette(this.themeColor, labels.length || 1)
                    : this.themeColor;
            }
            return { ...themed, label: themed.label ?? col, data: this._extractColumn(col) };
        });
        if (this._chartjsInstance) {
            this._chartjsInstance.destroy();
        }
        let options = style.options ?? {};
        if (!this.showTitle && options?.plugins?.title) {
            options = {
                ...options,
                plugins: { ...options.plugins, title: { ...options.plugins.title, display: false } },
            };
        }
        this._chartjsInstance = new Chart(canvas, {
            type: chartType,
            data: { labels: labels, datasets },
            options,
        });
    }
};
BasicGraph.styles = css `
    :host {
      display: block;
      width: 100%;
      height: 100%;
      min-height: 160px;
    }
    .container {
      width: 100%;
      height: 100%;
      min-height: 160px;
      position: relative;
    }
    canvas {
      display: block;
      max-width: 100%;
      width: 100% !important;
      height: 100% !important;
    }
    .error {
      color: #c0392b;
      padding: 12px;
      border: 1px solid #c0392b;
      background: #fceae8;
      border-radius: 4px;
      font-family: monospace;
      font-size: 12px;
      white-space: pre-wrap;
    }
  `;
__decorate([
    property({ type: Object })
], BasicGraph.prototype, "graph", void 0);
__decorate([
    property({ type: Object })
], BasicGraph.prototype, "data", void 0);
__decorate([
    property({ type: String, attribute: 'theme-color' })
], BasicGraph.prototype, "themeColor", void 0);
__decorate([
    property({ type: Boolean, attribute: 'show-title' })
], BasicGraph.prototype, "showTitle", void 0);
__decorate([
    property({ type: Boolean })
], BasicGraph.prototype, "print", void 0);
__decorate([
    state()
], BasicGraph.prototype, "_error", void 0);
BasicGraph = __decorate([
    customElement('basic-graph')
], BasicGraph);
export { BasicGraph };
//# sourceMappingURL=BasicGraph.js.map